import { useEffect, useState, useCallback } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useAuthStore } from '../store/useAuthStore';
import { useGameStore } from '../store/useGameStore';
import { getSocket } from '../lib/socket';
import { LobbyTableSummary, ActionType, PlayerPublic } from '../shared/types';
import { TableFelt } from '../components/TableFelt';
import { ActionButtons } from '../components/ActionButtons';
import { TableChat } from '../components/Chat';
import { BuyInModal } from '../components/BuyInModal';
import { LoadingState } from '../components/StateViews';
import { computeLegalActions } from '../lib/legalActions';
import { hapticNotification } from '../lib/telegram';

interface TableScreenProps {
  table: LobbyTableSummary;
  onLeave: () => void;
}

export function TableScreen({ table, onLeave }: TableScreenProps) {
  const { token, profile, setProfile } = useAuthStore();
  const { tableState, setTableState, chatMessages, addChatMessage, handHistory, addHandHistory, lastError, setError } =
    useGameStore();

  const [joined, setJoined] = useState(false);
  const [joining, setJoining] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [showBuyIn, setShowBuyIn] = useState(true);
  const [recentWinners, setRecentWinners] = useState<{ playerId: string; amount: number }[] | null>(null);
  const [winningCardKeys, setWinningCardKeys] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!token) return;
    const socket = getSocket(token);

    const onState = (s: typeof tableState) => s && setTableState(s);
    const onChat = (m: any) => addChatMessage(m);
    const onHistory = (h: any) => {
      addHandHistory(h);
      setRecentWinners(h.winners);
      const keys = new Set<string>();
      for (const w of h.winners) {
        for (const c of w.bestFiveCards ?? []) keys.add(`${c.rank}${c.suit}`);
      }
      setWinningCardKeys(keys);
      hapticNotification(h.winners.some((w: any) => w.playerId === profile?.id) ? 'success' : 'warning');
      setTimeout(() => {
        setRecentWinners(null);
        setWinningCardKeys(new Set());
      }, 3500);
    };
    const onErr = (e: { message: string }) => setError(e.message);

    socket.on('table:state', onState);
    socket.on('chat:message', onChat);
    socket.on('table:handHistory', onHistory);
    socket.on('error', onErr);

    return () => {
      socket.off('table:state', onState);
      socket.off('chat:message', onChat);
      socket.off('table:handHistory', onHistory);
      socket.off('error', onErr);
    };
  }, [token]);

  const handleBuyIn = useCallback(
    (amount: number) => {
      if (!token) return;
      setJoining(true);
      const socket = getSocket(token);
      socket.emit('table:join', { tableId: table.id, buyIn: amount }, (res: { ok: boolean; error?: string }) => {
        setJoining(false);
        if (res.ok) {
          setJoined(true);
          setShowBuyIn(false);
          if (profile) setProfile({ ...profile, chips: profile.chips - amount });
        } else {
          setError(res.error ?? 'Не удалось присоединиться');
        }
      });
    },
    [token, table.id, profile],
  );

  function leaveTable() {
    if (token) {
      getSocket(token).emit('table:leave', { tableId: table.id });
    }
    onLeave();
  }

  function performAction(type: ActionType, amount?: number) {
    if (!token) return;
    getSocket(token).emit('action:perform', { tableId: table.id, type, amount });
  }

  function sendChat(text: string) {
    if (!token) return;
    getSocket(token).emit('chat:send', { tableId: table.id, text });
  }

  if (showBuyIn) {
    return (
      <BuyInModal
        tableName={table.name}
        minBuyIn={table.minBuyIn}
        maxBuyIn={table.maxBuyIn}
        maxAvailable={profile?.chips ?? 0}
        onConfirm={handleBuyIn}
        onCancel={onLeave}
      />
    );
  }

  if (joining || !tableState) {
    return <LoadingState label="Присаживаемся за стол…" />;
  }

  const viewerId = profile!.id;
  const seats = buildRotatedSeats(tableState.players, table.maxSeats, viewerId);
  const legal = computeLegalActions(tableState, viewerId);

  return (
    <div className="min-h-screen flex flex-col">
      <header className="flex items-center justify-between px-3 py-2">
        <button onClick={leaveTable} className="text-white/50 text-sm px-2 py-1">
          ← Выйти
        </button>
        <div className="text-white/50 text-xs">
          {table.name} · {table.smallBlind}/{table.bigBlind}
        </div>
        <div className="text-gold-400 text-xs font-semibold tabular-nums">
          Раздача #{tableState.handNumber}
        </div>
      </header>

      <AnimatePresence>
        {lastError && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            onAnimationComplete={() => setTimeout(() => setError(null), 2200)}
            className="mx-3 mb-2 px-3 py-2 rounded-lg bg-rose-900/60 text-rose-200 text-xs text-center"
          >
            {lastError}
          </motion.div>
        )}
      </AnimatePresence>

      <div className="flex-1 flex flex-col justify-center px-2">
        <TableFelt
          players={seats}
          communityCards={tableState.communityCards}
          pots={tableState.pots}
          actingPlayerId={tableState.actingPlayerId}
          actionDeadline={tableState.actionDeadline}
          viewerPlayerId={viewerId}
          winners={recentWinners}
          winningCardKeys={winningCardKeys}
          onSitSeat={() => {}}
        />
      </div>

      <div className="px-3 pb-4 pt-2">
        {legal.types.length > 0 ? (
          <ActionButtons legal={legal} bigBlind={table.bigBlind} onAction={performAction} />
        ) : (
          <div className="text-center text-white/30 text-xs py-3">
            {tableState.street === 'showdown' ? 'Вскрытие карт…' : 'Ожидание хода других игроков…'}
          </div>
        )}
      </div>

      <TableChat
        messages={chatMessages}
        onSend={sendChat}
        viewerPlayerId={viewerId}
        open={chatOpen}
        onToggle={() => setChatOpen((v) => !v)}
      />
    </div>
  );
}

/**
 * Поворачивает массив мест так, чтобы место зрителя всегда оказывалось внизу (индекс 0) —
 * это стандартный UX для покерных клиентов: "я всегда смотрю на стол снизу".
 */
function buildRotatedSeats(players: PlayerPublic[], maxSeats: number, viewerId: string): (PlayerPublic | null)[] {
  const bySeat: (PlayerPublic | null)[] = new Array(maxSeats).fill(null);
  for (const p of players) bySeat[p.seat] = p;

  const viewerSeat = players.find((p) => p.id === viewerId)?.seat ?? 0;
  const rotated: (PlayerPublic | null)[] = [];
  for (let i = 0; i < maxSeats; i++) {
    rotated.push(bySeat[(viewerSeat + i) % maxSeats]);
  }
  return rotated;
}
